import { useState } from 'react';
import { CalendarPlus, Loader2, X } from 'lucide-react';
import { isSingleEventActivity } from 'shared';
import { useCreateSessions, type Activity } from '@/hooks/useActivities';
import { ApiValidationError } from '@/lib/api';
import {
  TOO_MANY_SESSIONS_MESSAGE,
  emptySessionForm,
  sessionServerErrors,
  toSessionCreateInput,
  tooManySessions,
  validateSessionForm,
  type SessionForm,
  type SessionFormErrors,
} from '@/lib/activityTime';
import SessionFields from './SessionFields';
import { useStaffOptions } from './useStaffOptions';

interface SessionCreateModalProps {
  activity: Activity;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

/**
 * Adds a session, or a run of them, to an activity that already exists. For
 * an open day left without a date it sets the one date it has.
 */
export default function SessionCreateModal({
  activity,
  onClose,
  onSuccess,
  onError,
}: SessionCreateModalProps) {
  const create = useCreateSessions();
  const staff = useStaffOptions();

  const [form, setForm] = useState<SessionForm>(() => emptySessionForm(activity.ownerUserId));
  const [errors, setErrors] = useState<SessionFormErrors>({});
  const openDay = isSingleEventActivity(activity.type);

  const handleChange = (next: SessionForm, touched: (keyof SessionForm)[]) => {
    setForm(next);
    setErrors((prev) => touched.reduce((acc, key) => ({ ...acc, [key]: undefined }), prev));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const clientErrors = validateSessionForm(form, openDay);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }
    if (tooManySessions(form)) {
      setErrors({ repeatUntil: TOO_MANY_SESSIONS_MESSAGE });
      return;
    }

    try {
      const result = await create.mutateAsync({
        activityId: activity.id,
        data: toSessionCreateInput(form),
      });
      onSuccess(
        openDay
          ? 'Data marcada com sucesso.'
          : result.total === 1
            ? 'Sessão criada com sucesso.'
            : `${result.total} sessões criadas com sucesso.`,
      );
      onClose();
    } catch (err: unknown) {
      if (err instanceof ApiValidationError) {
        setErrors(sessionServerErrors(err.errors));
        return;
      }
      onError(err instanceof Error ? err.message : 'Erro desconhecido.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-modal-title"
    >
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <CalendarPlus className="w-5 h-5 text-primary" />
            <h2 id="session-modal-title" className="text-lg font-semibold text-foreground">
              {openDay ? 'Marcar Data' : 'Nova Sessão'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-accent"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form
          id="session-form"
          onSubmit={handleSubmit}
          noValidate
          className="px-6 py-5 space-y-4 overflow-y-auto"
        >
          <SessionFields
            idPrefix="session"
            form={form}
            errors={errors}
            onChange={handleChange}
            leaders={{ options: staff.options, fallbackName: activity.ownerName }}
            allowRepeat={!openDay}
            multiDay={openDay}
          />
        </form>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={create.isPending}
            className="px-4 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="session-form"
            disabled={create.isPending}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {create.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {openDay ? 'Marcar Data' : form.repeat === 'none' ? 'Criar Sessão' : 'Criar Sessões'}
          </button>
        </div>
      </div>
    </div>
  );
}
